import type { ComponentProps, ComponentType, FC, ReactNode } from "react";
import MuiLink from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { Link as ReactRouterLink } from "react-router";
import type { IconProps } from "../shared/icons";
import { ExternalLinkIcon } from "../shared/icons";
import { multilineEllipsis } from "../shared/multilineEllipsis";
import { HACK_alignIconWithLabel } from "@privacy/ui";
import { Box } from "@mui/material";
import type { Theme } from "@mui/material/styles";

const styles: ComponentProps<typeof MuiLink>["sx"] = {
  ...multilineEllipsis(2),
  "lineHeight": 1,
  "fontSize": theme => theme.typography.pxToRem(14),
  "textDecoration": "none",
  "&:hover": { textDecoration: "underline" },
  "color": theme => `${theme.palette.text.primary} !important`,
};

export const Link: FC<{
  to: string;
  onClick?: () => void;
  children: ReactNode;
  Icon: ComponentType<IconProps>;
  sx?: ComponentProps<typeof MuiLink>["sx"];
}> = ({ to, children, Icon, sx, onClick }) => {
  return (
    <MuiLink
      component={ReactRouterLink}
      sx={{
        ...styles,
        ...sx,
        "display": "inline-flex",
        "alignItems": "center",
        "gap": 1,
        "&:focus-visible": FOCUS_VISIBLE_STYLES,
      }}
      onClick={e => {
        /** Since the grid row is also clickable */
        e.stopPropagation();
        onClick?.();
      }}
      to={to}
    >
      <Icon size={16} />
      <Box component="span" sx={{ display: "inline-block", ...HACK_alignIconWithLabel }}>
        {children}
      </Box>
    </MuiLink>
  );
};

export const FOCUS_VISIBLE_STYLES = (theme: Theme) => ({
  outline: "2px solid",
  outlineColor: theme.palette.primary.main,
  outlineOffset: "6px",
  borderRadius: "1px",
});

export const formatHref = (href: string) =>
  href.startsWith("https://") || href.startsWith("http://") ? href : `https://${href}`;

export const ExternalLink: FC<ComponentProps<typeof MuiLink> & { Icon?: ComponentType<IconProps> }> = ({
  Icon = ExternalLinkIcon,
  sx,
  children,
  title,
  href,
  ...rest
}) => {
  return (
    <Tooltip title={title}>
      <MuiLink
        {...rest}
        sx={{
          ...styles,
          "display": "inline-flex",
          "alignItems": "center",
          "gap": 1.5,
          ...sx,
          "&:focus-visible": FOCUS_VISIBLE_STYLES,
        }}
        onClick={e => e.stopPropagation()}
        target="_blank"
        href={href ? formatHref(href) : undefined}
      >
        <Icon size={16} />
        <Box component="span" sx={{ display: "inline-block", ...HACK_alignIconWithLabel }}>
          {children}
        </Box>
      </MuiLink>
    </Tooltip>
  );
};
